import { renderRulebookUserText, resolveMeterLevelIndex, useTranslation } from "@next-platform/client-common-core";
import { levelProgressFill } from "@next-platform/client-common-core/rulebook/meterFill";
import { formatMeterValue, type PublicMeterView } from "@next-platform/packages-ts-types";
import React, { useMemo } from "react";

import RulebookMeterBar from "@/components/Rulebook/RulebookMeterBar";
import Trans from "@/components/Trans/Trans";
import { useMeterColor } from "@/hooks";

import { HUD_BOX, HUD_MUTED } from "./hudClassNames";

type RulebookCounterValueProps = {
  meter: PublicMeterView;
  value: number;
  characterName?: string;
};

/**
 * Counter readout: "40 coins", or for counters shown as levels "Level 2 · Regular" with progress toward
 * the next level ("12 / 20 XP to level 3") and a bar.
 */
const RulebookCounterValue = ({ meter, value, characterName }: RulebookCounterValueProps) => {
  const { t } = useTranslation();
  const meterColor = useMeterColor(meter.color);
  const unit = meter.unit ?? "";
  const levelIndex = resolveMeterLevelIndex({ levels: meter.levels, value });
  const level = meter.levels[levelIndex];
  const next = meter.levels[levelIndex + 1];
  const isLevelDisplay = meter.display === "level" && level !== undefined;
  const hiddenLevelLabel = t("chat:rulebook.hud.sheet.hiddenLevel", { defaultValue: "???" });
  const values = useMemo(
    () => ({
      number: levelIndex + 1,
      label: level?.label !== undefined ? renderRulebookUserText({ text: level.label, characterName }) : hiddenLevelLabel,
      value: formatMeterValue(meter, value),
      next: next ? formatMeterValue(meter, next.from) : formatMeterValue(meter, 0),
      nextNumber: levelIndex + 2,
      unit,
    }),
    [levelIndex, level, next, value, unit, meter, characterName, hiddenLevelLabel],
  );

  return (
    <div data-testid="RulebookCounterValue" className={`flex flex-col gap-2 ${HUD_BOX}`}>
      <div className="flex items-baseline gap-2">
        <span className="text-lg font-bold">
          {isLevelDisplay ? (
            <Trans i18nKey="chat:rulebook.hud.sheet.counterLevel" defaults="Level {{number}} · {{label}}" values={values} />
          ) : (
            `${formatMeterValue(meter, value)} ${unit}`.trim()
          )}
        </span>
        {isLevelDisplay ? (
          <span className={`text-xs ${HUD_MUTED}`}>
            {next ? (
              <Trans
                i18nKey="chat:rulebook.hud.sheet.counterToNext"
                defaults="{{value}} / {{next}} {{unit}} to level {{nextNumber}}"
                values={values}
              />
            ) : (
              <Trans i18nKey="chat:rulebook.hud.sheet.counterTop" defaults="{{value}} {{unit}} · top level" values={values} />
            )}
          </span>
        ) : null}
      </div>
      {isLevelDisplay ? (
        <RulebookMeterBar
          size="progress"
          fill={levelProgressFill(meter, value)}
          color={meterColor}
          fillTestId={`RulebookCounterValue-${meter.id}-Progress`}
        />
      ) : null}
    </div>
  );
};

export default React.memo(RulebookCounterValue);
